/**
 * Microsoft Graph — online meetings (Teams) client.
 *
 * Wraps `/me/onlineMeetings` for creating Teams meeting URLs that get
 * attached to a calendar event when the appointment channel is `teams`.
 * Required scope: `OnlineMeetings.ReadWrite`.
 *
 * The unified Appointments landing reads join URLs directly off the
 * calendar event (Graph hydrates `onlineMeeting.joinUrl` for events that
 * were created `isOnlineMeeting: true`). This client is mainly for the
 * NewAppointment flow's `teams` branch — it creates the meeting first,
 * then drops the join URL into the new event body so it's clickable in
 * any calendar surface.
 */

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

interface GraphOnlineMeeting {
  id: string;
  joinWebUrl: string;
  subject?: string;
}

export interface CreateOnlineMeetingArgs {
  subject: string;
  /** ISO 8601 UTC. */
  startUtc: string;
  endUtc: string;
}

/**
 * Create a Teams meeting and return the join URL.
 * Throws on failure — callers downgrade to "phone" if the user lacks scope.
 */
export async function createOnlineMeeting(token: string, args: CreateOnlineMeetingArgs): Promise<{
  joinWebUrl: string;
  meetingId: string;
}> {
  const res = await fetch(`${GRAPH_BASE}/me/onlineMeetings`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      startDateTime: args.startUtc,
      endDateTime: args.endUtc,
      subject: args.subject,
    }),
  });
  if (!res.ok) {
    throw new Error(`Graph POST /me/onlineMeetings failed (${res.status})`);
  }
  const json = (await res.json()) as GraphOnlineMeeting;
  return { joinWebUrl: json.joinWebUrl, meetingId: json.id };
}

/**
 * Open a Teams join URL — uses the Teams SDK's `app.openLink` when running
 * inside the Teams desktop client (so it joins in the existing Teams shell
 * instead of spawning a new browser); falls back to `window.open` otherwise.
 */
export async function openTeamsJoinLink(joinWebUrl: string): Promise<void> {
  if (!joinWebUrl) return;
  // Lazy-import the Teams SDK so this module stays importable in pure SSR /
  // unit-test contexts.
  try {
    const { app } = await import('@microsoft/teams-js');
    if (app.isInitialized()) {
      await app.openLink(joinWebUrl);
      return;
    }
  } catch {
    // ignored — fall through to browser
  }
  window.open(joinWebUrl, '_blank', 'noopener,noreferrer');
}
